import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useActions } from '../../../hooks/useActions';
import { useProcessing } from '../../../hooks/useProcessing';
import { ProductType } from '../../../models/ProductType';
import { ProductSubscriptionRenewPageParamsType } from '../../../models/types/RoutePath';
import { clearOrganizationContracts } from '../../../store/redux-slices/contracts';
import { organizationSelector } from '../../../store/redux-slices/organizations/selectors';
import {
  ORGANIZATION_LOADING_PROCESSING,
  SUBSCRIPTION_RENEWAL_SUGGESTION_LOADING_PROCESSING,
  SR_PRICING_LOADING_PROCESSING,
} from '../../../store/redux-slices/processes/constants';
import { clearSubscriptionHelperData } from '../../../store/redux-slices/subscriptions/common';
import { clearSRSubscriptionHelperData } from '../../../store/redux-slices/subscriptions/securityResponse';
import { srSubscriptionRenewalSuggestionSelector } from '../../../store/redux-slices/subscriptions/securityResponse/selectors';
import { loadOrganizationContracts } from '../../../store/saga-slices/contracts';
import { loadOrganization } from '../../../store/saga-slices/organizations';
import { srLoadStandardPricing } from '../../../store/saga-slices/pricing/securityResponse';
import { ILoadDatesForNewSubscriptionPayload } from '../../../store/saga-slices/subscriptions/common/models';
import {
  loadDatesForNewSRSubscription,
  loadSRSubscriptionRenewalSuggestion,
} from '../../../store/saga-slices/subscriptions/securityResponse';
import { ILoadSRSubscriptionRenewalSuggestionPayload } from '../../../store/saga-slices/subscriptions/securityResponse/models';

export const useSubscriptionSRRenew = () => {
  const { organizationId, subscriptionId } =
    useParams<ProductSubscriptionRenewPageParamsType>();

  const LoadOrganization = useActions(loadOrganization);
  const LoadOrganizationContracts = useActions(loadOrganizationContracts);
  const ClearOrganizationContracts = useActions(clearOrganizationContracts);
  const LoadStandardPricing = useActions(srLoadStandardPricing);
  const LoadDatesForNewSubscription = useActions(loadDatesForNewSRSubscription);
  const ClearSRSubscriptionHelperData = useActions(
    clearSRSubscriptionHelperData,
  );
  const ClearSubscriptionHelperData = useActions(clearSubscriptionHelperData);

  const LoadSubscriptionRenewalSuggestion = useActions(
    loadSRSubscriptionRenewalSuggestion,
  );

  const subscriptionRenewalSuggestion = useSelector(
    srSubscriptionRenewalSuggestionSelector,
  );

  const isOrganizationDataLoading = useProcessing(
    ORGANIZATION_LOADING_PROCESSING,
  );

  const isSuggestionDataLoading = useProcessing(
    SUBSCRIPTION_RENEWAL_SUGGESTION_LOADING_PROCESSING,
  );

  const isPricingDataLoading = useProcessing(SR_PRICING_LOADING_PROCESSING);
  const organization = useSelector(organizationSelector);

  const loadSubscriptionRenewalSuggestionHandler = useCallback(() => {
    const payload: ILoadSRSubscriptionRenewalSuggestionPayload = {
      organizationId,
      subscriptionId,
    };
    LoadSubscriptionRenewalSuggestion(payload);
  }, [LoadSubscriptionRenewalSuggestion, organizationId, subscriptionId]);

  const loadDatesForNewSubscriptionHandler = useCallback(
    (value: string, forceLoad = false) => {
      if (!value && !forceLoad) {
        return;
      }

      const loadDatesForNewSubscriptionPayload: ILoadDatesForNewSubscriptionPayload =
        {
          organizationId,
          contractId: value,
        };
      LoadDatesForNewSubscription(loadDatesForNewSubscriptionPayload);
    },
    [LoadDatesForNewSubscription, organizationId],
  );

  useEffect(() => {
    LoadOrganizationContracts({ organizationId });
    LoadOrganization(organizationId);

    loadSubscriptionRenewalSuggestionHandler();
    LoadStandardPricing({ productType: ProductType.SecurityResponse });
  }, [
    LoadOrganization,
    LoadOrganizationContracts,
    LoadSubscriptionRenewalSuggestion,
    organizationId,
    subscriptionId,
    LoadStandardPricing,
    loadSubscriptionRenewalSuggestionHandler,
  ]);

  useEffect(
    () => () => {
      ClearSRSubscriptionHelperData();
      ClearOrganizationContracts();
      ClearSubscriptionHelperData();
    },
    [
      ClearOrganizationContracts,
      ClearSRSubscriptionHelperData,
      ClearSubscriptionHelperData,
    ],
  );

  return {
    isPricingDataLoading,
    isOrganizationDataLoading,
    isSuggestionDataLoading,
    organization,
    organizationId,
    subscriptionId,
    loadSubscriptionRenewalSuggestionHandler,
    subscriptionRenewalSuggestion,
    loadDatesForNewSubscriptionHandler,
  };
};
